// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { xCommonLoadingOverlay } from "@turnerBlueprint/App/Handlers/XNG/Common";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import React, { FC, useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

/**
 * Steps:
 * Add "hideLoadingOverlay" code inside PreStyle/globalStyle
 */
export const hideLoadingOverlay = css`
    #react-app.remove-ootb-spinners {
        div[data-test-selector="loadingOverlaySpinner"],
        svg[class^="LoadingSpinnerStyle"] {
            visibility: hidden;
            width: 0;
            height: 0;
            position: absolute;
            top: -10000px;
        }
    }
`;

export const zIndexLoadingOverlay = 1500;

/**
 * Add this style in XDL/GlobalStyle
 */
export const xngLoadingOverlayUtilitiesStyles = css`
    #xng-loading-overlay {
        width: 100%;
        position: fixed;
        height: 100%;
        z-index: ${zIndexLoadingOverlay};
        top: 0;
        bottom: 0;
        @media print {
            display: none;
        }
    }
`;

/**
 * Global component uses in Header
 * @constructor
 */
const XngLoadingOverlayUtilities: FC = () => {
    const { isErrorModalOpen, pages, xLoadingOverlay, isSigningIn } = useAppSelector(state => ({
        isErrorModalOpen: state.context.isErrorModalOpen,
        pages: state.data.pages,
        xLoadingOverlay: (state as CustomAppState).xCommon.loadingOverlay,
        isSigningIn: state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest,
    }));

    const isShowing = () => isSigningIn || (xLoadingOverlay && xLoadingOverlay?.length > 0);
    const [showLoading, setShowLoading] = useState(isShowing());

    const dispatch = useDispatch();

    useEffect(() => {
        const showXngLoading = isShowing();
        setShowLoading(showXngLoading);

        if (typeof document === "undefined") {
            return;
        }

        /** To hide all loadingOverlay and spinner
         * We added "hideLoadingOverlay" inside PreStyle/globalStyle
         */
        const element = document.getElementById("react-app");
        if (showXngLoading) {
            element?.classList.add(`remove-ootb-spinners`);
        } else {
            element?.classList.remove(`remove-ootb-spinners`);
        }
    }, [isSigningIn, xLoadingOverlay]);

    /** To close LoadingOverlay when the page redirect to another path */
    useEffect(() => {
        dispatch(xCommonLoadingOverlay({ key: "clear", value: "" }));
    }, [pages.location]);

    /** To close LoadingOverlay when ErrorModal open */
    useEffect(() => {
        if (isErrorModalOpen) {
            dispatch(xCommonLoadingOverlay({ key: "clear", value: "" }));
        }
    }, [isErrorModalOpen]);

    const LoadingElement = ({ showLoading }: { showLoading?: boolean }) => (
        <>
            {showLoading && (
                <LoadingOverlay
                    id="xng-loading-overlay"
                    data-test-selector="main-loading-overlay"
                    loading={true}
                    css={css`
                        @media print {
                            display: none;
                        }
                    `}
                />
            )}
        </>
    );

    if (IS_SERVER_SIDE) {
        return <LoadingElement showLoading={true} />;
    }

    return ReactDOM.createPortal(<LoadingElement showLoading={showLoading} />, document.body);
};

export default XngLoadingOverlayUtilities;
